<template>
  <header class="modern-navigation">
    <nav class="nav-container">

      <section class="nav-left">
        <!-- Menú Toggle para Mobile -->
        <button
          v-if="isLogged && !navegacionBloqueada"
          class="mobile-menu-toggle"
          @click="mobileMenuOpen = !mobileMenuOpen"
          :class="{ 'active': mobileMenuOpen }"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
  
        <!-- Logo -->
        <div class="nav-logo">
          <!-- Con una encuesta obligatoria pendiente el logo no navega -->
          <img v-if="navegacionBloqueada" src="@/assets/logo_small_white.png" alt="Logo" class="logo-img">
          <router-link v-else :to="{ name: 'home' }">
            <img src="@/assets/logo_small_white.png" alt="Logo" class="logo-img">
          </router-link>
        </div>
      </section>

      <!-- Navegación Principal -->
      <div class="nav-links" :class="{ 'mobile-open': mobileMenuOpen }" v-if="isLogged && !navegacionBloqueada">
        <router-link :to="{ name: 'home' }" exact class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Inicio')">
          <img src="@/assets/custom-icons/home_2.svg" alt="Inicio" class="nav-icon" />
          <span>Inicio</span>
        </router-link>

        <router-link :to="{ name: 'programar' }" class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Programar')">
          <img src="@/assets/custom-icons/book_2.svg" alt="Programar" class="nav-icon" />
          <span>Programar</span>
        </router-link>

        <router-link :to="{ name: 'mis_clases' }" exact class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Mis Clases')">
          <img src="@/assets/custom-icons/check_2.svg" alt="Mis Clases" class="nav-icon" />
          <span>Mis Clases</span>
        </router-link>

        <router-link :to="{ name: 'registro_academico' }" class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Progreso')">
          <img src="@/assets/custom-icons/progreso_2.svg" alt="Progreso" class="nav-icon" />
          <span>Progreso</span>
        </router-link>

        <router-link :to="{ name: 'evaluaciones' }" class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Evaluaciones')">
          <img src="@/assets/custom-icons/check_agenda_2.svg" alt="Evaluaciones" class="nav-icon" />
          <span>Evaluaciones</span>
        </router-link>

        <router-link :to="{ name: 'apoyo_estudiantil' }" class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Apoyo')">
          <img src="@/assets/custom-icons/bombillo_2.svg" alt="Apoyo estudiantil" class="nav-icon" />
          <span>Apoyo</span>
        </router-link>

        <router-link v-if="tutoriasHabilitadas" :to="{ name: 'tutoria' }" class="nav-link" @click.native="closeMobileMenu"
          v-b-tooltip.hover.bottom="tooltipText('Tutorías')">
          <img src="@/assets/custom-icons/tutorias_2.svg" alt="Tutorías" class="nav-icon" />
          <span>Tutorías</span>
        </router-link>
      </div>

      <!-- Acciones de Usuario -->
      <div class="nav-actions" v-if="isLogged">
        <!-- Toggle Tema -->
        <button 
          class="theme-toggle-btn icon-btn" 
          @click="theme.toggleTheme" 
          title="Cambiar tema"
        >
          <i class="ri-sun-line"></i>
        </button>

        <!-- Notificaciones y perfil (agrupados para poder resaltarlos juntos en el recorrido) -->
        <div class="nav-account">
        <div class="notification-menu" v-if="!navegacionBloqueada" v-click-outside="closeNotificationMenu">
          <button class="icon-btn" @click="notificationMenuOpen = !notificationMenuOpen">
            <i class="ri-notification-3-line"></i>
            <span class="notification-badge" v-if="contadorNotificaciones > 0">{{ contadorNotificaciones }}</span>
          </button>

          <!-- Dropdown de Notificaciones -->
          <transition name="dropdown">
            <div class="notification-dropdown" v-if="notificationMenuOpen">
              <div class="dropdown-header-simple">
                <h3>Notificaciones</h3>
                <button 
                  v-if="notificacionesNoLeidas.length > 0" 
                  @click="marcarTodasLeidas" 
                  class="btn-mark-all-read"
                  title="Marcar todas como leídas"
                >
                  <i class="ri-check-double-line"></i>
                </button>
              </div>
              
              <!-- Loading state -->
              <div v-if="loadingNotificaciones" class="notification-loading">
                <div class="spinner-small"></div>
                <p>Cargando notificaciones...</p>
              </div>

              <!-- Lista de notificaciones -->
              <div v-else-if="notificacionesRecientes && notificacionesRecientes.length > 0" class="notification-list">
                <div 
                  v-for="notificacion in notificacionesRecientes" 
                  :key="notificacion.id"
                  class="notification-item"
                  :class="[
                    { 'unread': notificacion.leida === 0 },
                    { 'clickable': esNotificacionClickeable(notificacion.tipo) }
                  ]"
                  @click="handleNotificacionClick(notificacion)"
                >
                  <div class="notification-icon" :class="'type-' + getNotificacionTipo(notificacion.tipo)">
                    <i :class="getNotificacionIcono(notificacion.tipo)"></i>
                  </div>
                  <div class="notification-content">
                    <p class="notification-title">{{ notificacion.titulo }}</p>
                    <p class="notification-message">{{ notificacion.mensaje }}</p>
                    <span class="notification-time">{{ formatearFecha(notificacion.created_at) }}</span>
                  </div>
                  <div class="notification-indicator" v-if="notificacion.leida === 0"></div>
                </div>
              </div>

              <!-- Estado vacío -->
              <div v-else class="notification-empty">
                <i class="ri-notification-off-line"></i>
                <p>No tienes notificaciones nuevas</p>
              </div>

              <div class="dropdown-footer">
                <router-link :to="{ name: 'notificaciones' }" class="view-all-link" @click.native="closeNotificationMenu">
                  Ver todas las notificaciones
                </router-link>
              </div>
            </div>
          </transition>
        </div>

        <!-- Avatar y Dropdown -->
        <div class="user-menu" v-click-outside="closeUserMenu">
          <button class="user-avatar-btn" @click="userMenuOpen = !userMenuOpen">
            <img 
              v-if="hasUserImage"
              :src="userAvatar" 
              :alt="userName"
              class="avatar-img"
              @error="handleImageError"
            >
            <div 
              v-else
              class="avatar-initials"
              :style="{ backgroundColor: avatarColor }"
            >
              {{ userInitials }}
            </div>
            <span class="user-name">{{ userName }}</span>
            <i class="ri-arrow-down-s-line chevron"></i>
          </button>

          <!-- Dropdown Menu -->
          <transition name="dropdown">
            <div class="user-dropdown" v-if="userMenuOpen">
              <div class="dropdown-header">
                <img 
                  v-if="hasUserImage"
                  :src="userAvatar" 
                  :alt="userFullName" 
                  class="dropdown-avatar" 
                  @error="handleImageError"
                >
                <div 
                  v-else
                  class="dropdown-avatar avatar-initials-large"
                  :style="{ backgroundColor: avatarColor }"
                >
                  {{ userInitials }}
                </div>
                <div class="dropdown-info">
                  <p class="dropdown-name">{{ userFullName }}</p>
                  <p class="dropdown-email">{{ userEmail }}</p>
                </div>
              </div>
              <div class="dropdown-divider"></div>
              <!-- Cerrar sesión siempre está disponible; Mi Perfil se oculta con una encuesta obligatoria pendiente -->
              <template v-if="!navegacionBloqueada">
                <router-link :to="{ name: 'profile' }" class="dropdown-item" @click.native="closeUserMenu">
                  <i class="ri-user-line"></i>
                  <span>Mi Perfil</span>
                </router-link>
                <div class="dropdown-divider"></div>
              </template>
              <button class="dropdown-item logout-btn" @click="processLogout">
                <i class="ri-logout-box-line"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </transition>
        </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
  import {mapState, mapGetters, mapMutations, mapActions} from 'vuex';
  
  export default {
    components: {},
    name: 'MainHeader',
    inject: {
      theme: {
        from: 'theme',
        default: () => ({ isDarkTheme: false, toggleTheme: () => {} })
      }
    },
    data() {
      return {
        userMenuOpen: false,
        mobileMenuOpen: false,
        notificationMenuOpen: false,
        iconsOnly: false
      }
    },
    mounted() {
      // Mismo rango en que el CSS oculta los textos del menú (solo desktop)
      this.iconsOnlyQuery = window.matchMedia('(min-width: 769px) and (max-width: 1295px)');
      this.iconsOnly = this.iconsOnlyQuery.matches;
      this.iconsOnlyQuery.addListener(this.updateIconsOnly);
    },
    beforeDestroy() {
      if (this.iconsOnlyQuery) {
        this.iconsOnlyQuery.removeListener(this.updateIconsOnly);
      }
    },
    directives: {
      'click-outside': {
        bind: function (el, binding, vnode) {
          el.clickOutsideEvent = function (event) {
            if (!(el === event.target || el.contains(event.target))) {
              vnode.context[binding.expression](event);
            }
          };
          document.body.addEventListener('click', el.clickOutsideEvent)
        },
        unbind: function (el) {
          document.body.removeEventListener('click', el.clickOutsideEvent)
        },
      }
    },
    computed:{
      ...mapState({
        isLogged: state => state.auth.logged,
        user: state => state.auth.user, 
        variables: state => state.student.variables,
        banners: state => state.banner.banners,
        notificaciones: state => state.notificaciones.notificaciones,
        loadingNotificaciones: state => state.notificaciones.loading
      }),
      ...mapGetters({
        navegacionBloqueada: 'encuestaSatisfaccion/navegacionBloqueada'
      }),
      tutoriasHabilitadas() {
        return this.variables && this.variables.habilitar_tutoria == 1 ? true : false;
      },
      notificacionesNoLeidas() {
        if (!this.notificaciones) return [];
        return this.notificaciones.filter(n => n.leida === 0);
      },
      notificacionesRecientes() {
        if (!this.notificaciones) return [];
        return this.notificaciones.slice(0, 5);
      },
      contadorNotificaciones() {
        return this.notificacionesNoLeidas.length;
      },
      userName() {
        // Retorna solo el primer nombre para mostrar en el botón
        if (this.user && this.user.data) {
          return this.user.data.primer_nombre;
        }
        return 'Usuario';
      },
      userFullName() {
        // Retorna el nombre completo para el dropdown
        if (this.user && this.user.data) {
          return this.user.data.primer_nombre + ' ' + this.user.data.primer_apellido;
        }
        return 'Usuario';
      },
      userEmail() {
        if (this.user && this.user.data) {
          return this.user.data.email;
        }
        return '';
      },
      userAvatar() {
        // Retorna la URL de la imagen del usuario si existe
        if (this.user && this.user.data) {
          return this.user.data.avatar || this.user.data.photo || this.user.data.image || null;
        }
        return null;
      },
      hasUserImage() {
        return !!this.userAvatar;
      },
      avatarColor() {
        // Genera un color basado en las iniciales usando variables CSS
        const colors = [
          getComputedStyle(document.documentElement).getPropertyValue('--app-primary-blue').trim(),
          getComputedStyle(document.documentElement).getPropertyValue('--app-color-cyan-light').trim(),
          getComputedStyle(document.documentElement).getPropertyValue('--app-color-cyan').trim(),
          getComputedStyle(document.documentElement).getPropertyValue('--app-color-purple').trim()
        ];
        const colorIndex = this.userInitials.charCodeAt(0) % colors.length;
        return colors[colorIndex];
      },
      userInitials() {
        if (this.user && this.user.data) {
          const nombre = this.user.data.primer_nombre ? this.user.data.primer_nombre.trim() : null;
          const apellido = this.user.data.primer_apellido ? this.user.data.primer_apellido.trim() : null;

          if(nombre && apellido) {
            return nombre.charAt(0).toUpperCase() + apellido.charAt(0).toUpperCase();
          } else if (nombre) {
            return nombre.charAt(0).toUpperCase();
          } else if (apellido) {
            return apellido.charAt(0).toUpperCase();
          }
        }
        return 'U';
      }
    },
    methods: {
      ...mapActions({
        logout: 'auth/logout',
        marcarComoLeida: 'notificaciones/marcarComoLeida',
        marcarTodasComoLeidas: 'notificaciones/marcarTodasComoLeidas'
      }),
      ...mapMutations({
        clear : 'academico/clear'
      }),
      processLogout () {
        this.clear()
        this.logout().then(() => {
          this.$router.push('/login')
        })
      },
      closeUserMenu() {
        this.userMenuOpen = false;
      },
      closeMobileMenu() {
        this.mobileMenuOpen = false;
      },
      updateIconsOnly(event) {
        this.iconsOnly = event.matches;
      },
      tooltipText(label) {
        return this.iconsOnly ? label : '';
      },
      closeNotificationMenu() {
        this.notificationMenuOpen = false;
      },
      async marcarTodasLeidas() {
        try {
          await this.marcarTodasComoLeidas();
          this.$toast?.success?.('Todas las notificaciones han sido marcadas como leídas');
        } catch (error) {
          console.error('Error al marcar todas las notificaciones:', error);
          this.$toast?.error?.('Error al marcar las notificaciones como leídas');
        }
      },
      esNotificacionClickeable(tipo) {
        return tipo === 'clase_agendada' || tipo === 'clase_cancelada';
      },
      async handleNotificacionClick(notificacion) {
        // Marcar como leída si no lo está
        if (notificacion.leida === 0) {
          try {
            await this.marcarComoLeida(notificacion.id);
          } catch (error) {
            console.error('Error al marcar notificación como leída:', error);
          }
        }

        // Cerrar el menú de notificaciones
        this.closeNotificationMenu();

        // Redirigir si es una notificación clickeable
        if (this.esNotificacionClickeable(notificacion.tipo)) {
          this.$router.push({ name: 'mis_clases' });
        }
      },
      getNotificacionIcono(tipo) {
        const iconos = {
          'clase_agendada': 'ri-calendar-check-line',
          'clase_cancelada': 'ri-calendar-close-line',
          'evaluacion': 'ri-file-list-line',
          'tutoria': 'ri-user-star-line',
          'mensaje': 'ri-message-3-line',
          'aviso': 'ri-notification-3-line',
          'default': 'ri-information-line'
        };
        return iconos[tipo] || iconos.default;
      },
      getNotificacionTipo(tipo) {
        const tipos = {
          'clase_agendada': 'success',
          'clase_cancelada': 'danger',
          'evaluacion': 'info',
          'tutoria': 'info',
          'mensaje': 'default',
          'aviso': 'warning',
          'default': 'default'
        };
        return tipos[tipo] || tipos.default;
      },
      formatearFecha(fecha) {
        if (!fecha) return '';
        try {
          const date = new Date(fecha);
          const ahora = new Date();
          const diffMs = ahora - date;
          const diffMins = Math.floor(diffMs / 60000);
          const diffHoras = Math.floor(diffMs / 3600000);
          const diffDias = Math.floor(diffMs / 86400000);
          
          if (diffMins < 1) return 'Hace un momento';
          if (diffMins < 60) return `Hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
          if (diffHoras < 24) return `Hace ${diffHoras} ${diffHoras === 1 ? 'hora' : 'horas'}`;
          if (diffDias < 7) return `Hace ${diffDias} ${diffDias === 1 ? 'día' : 'días'}`;
          
          return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
        } catch (e) {
          return '';
        }
      },
      handleImageError(event) {
        // Ocultar la imagen y mostrar las iniciales
        event.target.style.display = 'none';
      }
    }
  }
</script>

<style scoped>

/* Header Principal - Usa variables globales directamente */
.modern-navigation {
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(20px);
  background: var(--theme-header-bg);
  border-bottom: 1px solid var(--theme-nav-border);
  box-shadow: var(--theme-shadow-md);
  transition: background 0.3s ease, border-color 0.3s ease;
}

.nav-container {
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  gap: 1rem;
}

.nav-left{
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Logo */
.nav-logo {
  flex-shrink: 0;
}

.logo-img {
  width: 100px;
  height: auto;
  transition: transform 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
}

/* Mobile Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 32px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
}

.mobile-menu-toggle span {
  width: 100%;
  height: 3px;
  background: var(--theme-nav-text);
  border-radius: 10px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.mobile-menu-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translateY(9px);
}

.mobile-menu-toggle.active span:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translateY(-9px);
}

/* Navegación Links */
.nav-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.625rem;
  color: var(--theme-nav-text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

/* Efecto de brillo en hover */
.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.nav-link:hover::before {
  left: 100%;
}

.nav-link svg,
.nav-link .nav-icon {
  flex-shrink: 0;
  opacity: 0.8;
  transition: all 0.3s ease;
}

/* Estilos específicos para iconos PNG */
.nav-link .nav-icon {
  width: 30px;
  height: 30px;
  font-size: 30px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  object-fit: contain;
  filter: brightness(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.nav-link:hover {
  color: var(--theme-nav-text);
  background: var(--theme-nav-hover);
  text-decoration: none;
  transform: translateY(-2px);
}

.nav-link:hover svg,
.nav-link:hover .nav-icon {
  opacity: 1;
  transform: scale(1.1);
}

.nav-link:hover .nav-icon {
  filter: brightness(1.1) drop-shadow(0 2px 4px rgba(255, 255, 255, 0.3));
}

/* Estilo para link activo (exact match) */
.nav-link.router-link-exact-active {
  color: #ffffff;
  background: var(--theme-nav-active);
  box-shadow: 0 4px 16px rgba(13, 78, 140, 0.4), 0 0 0 1px rgba(13, 78, 140, 0.2);
  font-weight: 600;
  transform: translateY(-1px);
}

.nav-link.router-link-exact-active svg,
.nav-link.router-link-exact-active .nav-icon {
  opacity: 1;
  transform: scale(1.05);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.nav-link.router-link-exact-active .nav-icon {
  filter: brightness(1.1) drop-shadow(0 2px 6px rgba(255, 255, 255, 0.4));
}

/* Animación de pulso sutil para el item activo */
@keyframes subtle-pulse {
  0%, 100% {
    box-shadow: 0 4px 16px rgba(13, 78, 140, 0.4), 0 0 0 1px rgba(13, 78, 140, 0.2);
  }
  50% {
    box-shadow: 0 4px 20px rgba(13, 78, 140, 0.5), 0 0 0 1px rgba(13, 78, 140, 0.3);
  }
}

.nav-link.router-link-exact-active {
  animation: subtle-pulse 3s ease-in-out infinite;
}

/* Estilos específicos para tema claro - Mismo efecto que hover */
html.light-theme .nav-link.router-link-exact-active,
.light-theme .nav-link.router-link-exact-active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

html.light-theme .nav-link.router-link-exact-active svg,
.light-theme .nav-link.router-link-exact-active svg,
html.light-theme .nav-link.router-link-exact-active .nav-icon,
.light-theme .nav-link.router-link-exact-active .nav-icon {
  color: #ffffff;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

html.light-theme .nav-link.router-link-exact-active .nav-icon,
.light-theme .nav-link.router-link-exact-active .nav-icon {
  filter: brightness(1) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

/* Animación de pulso para tema claro - Deshabilitada, mismo estilo que hover */
@keyframes subtle-pulse-light {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  50% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

html.light-theme .nav-link.router-link-exact-active,
.light-theme .nav-link.router-link-exact-active {
  animation: none;
}

/* Hover mejorado para tema claro */
html.light-theme .nav-link:hover,
.light-theme .nav-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Efecto de brillo mejorado para tema claro */
html.light-theme .nav-link::before,
.light-theme .nav-link::before {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
}

/* Acciones de Usuario */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-account {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Botón de Notificaciones */
.notification-menu {
  position: relative;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--theme-nav-border);
  border-radius: 12px;
  color: var(--theme-nav-text);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
  text-decoration: none;
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--theme-gradient-secondary);
  color: white;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--theme-header-bg);
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Dropdown de Notificaciones */
.notification-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 380px;
  max-width: calc(100vw - 2rem);
  background: var(--theme-bg-secondary);
  border: 1px solid var(--theme-border-color);
  border-radius: 16px;
  box-shadow: var(--theme-shadow-xl);
  overflow: hidden;
  z-index: 1000;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.dropdown-header-simple {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid var(--theme-border-color);
  gap: 1rem;
}

.dropdown-header-simple h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--theme-text-primary);
  flex: 1;
}

.btn-mark-all-read {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(13, 78, 140, 0.1);
  border: 1px solid rgba(13, 78, 140, 0.3);
  border-radius: 8px;
  color: var(--app-primary-blue);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-mark-all-read:hover {
  background: rgba(13, 78, 140, 0.2);
  transform: scale(1.05);
}

.btn-mark-all-read i {
  font-size: 1.1rem;
}

/* Loading state */
.notification-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.spinner-small {
  width: 32px;
  height: 32px;
  border: 3px solid var(--theme-border-color);
  border-top-color: var(--app-primary-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notification-loading p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--theme-text-secondary);
}

/* Lista de notificaciones */
.notification-list {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
}

.notification-list::-webkit-scrollbar {
  width: 6px;
}

.notification-list::-webkit-scrollbar-track {
  background: var(--theme-bg-tertiary);
}

.notification-list::-webkit-scrollbar-thumb {
  background: var(--theme-border-color-strong);
  border-radius: 10px;
}

.notification-list::-webkit-scrollbar-thumb:hover {
  background: var(--theme-text-muted);
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--theme-border-color-light);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background: var(--theme-hover-bg-light);
}

.notification-item.unread {
  background: var(--theme-hover-bg);
}

.notification-item.unread:hover {
  background: var(--theme-active-bg);
}

.notification-item.clickable {
  cursor: pointer;
}

.notification-item.clickable:hover {
  background: var(--theme-hover-bg);
  transform: translateX(4px);
}

.notification-item.clickable.unread:hover {
  background: var(--theme-active-bg);
}

.notification-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-bg-tertiary);
}

.notification-icon.type-success {
  background: rgba(76, 217, 100, 0.2);
  color: var(--theme-color-success);
}

.notification-icon.type-danger {
  background: rgba(255, 59, 48, 0.2);
  color: var(--theme-color-danger);
}

.notification-icon.type-warning {
  background: rgba(255, 149, 0, 0.2);
  color: var(--theme-color-warning);
}

.notification-icon.type-info {
  background: rgba(13, 78, 140, 0.2);
  color: var(--app-primary-blue);
}

.notification-icon.type-default {
  background: var(--theme-bg-tertiary);
  color: var(--theme-text-secondary);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--theme-text-primary);
  line-height: 1.3;
}

.notification-message {
  margin: 0 0 0.5rem 0;
  font-size: 0.85rem;
  color: var(--theme-text-secondary);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-time {
  font-size: 0.75rem;
  color: var(--theme-text-muted);
  opacity: 0.8;
}

.notification-indicator {
  position: absolute;
  top: 1.25rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: var(--app-primary-blue);
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(13, 78, 140, 0.5);
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2rem;
  text-align: center;
}

.summary-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 78, 140, 0.2) 0%, rgba(79, 172, 254, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  animation: pulse-icon 2s ease-in-out infinite;
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(13, 78, 140, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 8px rgba(13, 78, 140, 0);
  }
}

.summary-icon i {
  font-size: 2rem;
  color: var(--app-primary-blue);
}

.summary-text {
  margin: 0;
  font-size: 1rem;
  color: var(--theme-text-primary);
  line-height: 1.5;
}

.summary-text strong {
  color: var(--app-primary-blue);
  font-size: 1.125rem;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2rem;
  text-align: center;
  color: var(--theme-text-secondary);
}

.notification-empty i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
  color: var(--theme-text-muted);
}

.notification-empty p {
  margin: 0;
  font-size: 0.95rem;
}

.dropdown-footer {
  border-top: 1px solid var(--theme-border-color);
  padding: 0.75rem 1.25rem;
  text-align: center;
}

.view-all-link {
  display: inline-block;
  color: var(--app-primary-blue);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 0.5rem 1rem;
  border-radius: 8px;
}

.view-all-link:hover {
  background: rgba(13, 78, 140, 0.1);
  color: var(--app-primary-blue);
  text-decoration: none;
}
.theme-toggle-btn {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05) !important;
}

.theme-toggle-btn svg {
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  background: rgba(13, 78, 140, 0.15) !important;
}

.theme-toggle-btn:hover svg {
  transform: rotate(15deg) scale(1.1);
  filter: drop-shadow(0 0 8px rgba(13, 78, 140, 0.5));
}

.theme-toggle-btn:active svg {
  transform: rotate(180deg) scale(0.95);
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--theme-nav-border);
  border-radius: 12px;
  color: var(--theme-nav-text);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
  text-decoration: none;
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--theme-gradient-secondary);
  color: white;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--theme-header-bg);
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Botón de Toggle Tema */
.theme-toggle-btn {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05) !important;
}

.theme-toggle-btn svg {
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  background: rgba(13, 78, 140, 0.15) !important;
}

.theme-toggle-btn:hover svg {
  transform: rotate(15deg) scale(1.1);
  filter: drop-shadow(0 0 8px rgba(13, 78, 140, 0.5));
}

.theme-toggle-btn:active svg {
  transform: rotate(180deg) scale(0.95);
}

/* Avatar y Menú de Usuario */
.user-menu {
  position: relative;
}

.user-avatar-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0.75rem 0.375rem 0.375rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--theme-nav-border);
  border-radius: 12px;
  color: var(--theme-nav-text);
  cursor: pointer;
  transition: all 0.3s ease;
}

.user-avatar-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(13, 78, 140, 0.5);
  background: var(--theme-gradient-primary);
}

/* Avatar con iniciales */
.avatar-initials {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--app-font-family);
  text-transform: uppercase;
  border: 2px solid rgba(13, 78, 140, 0.5);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.avatar-initials-large {
  width: 48px;
  height: 48px;
  font-size: 1.1rem;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  transition: transform 0.3s ease;
}

.user-avatar-btn:hover .chevron {
  transform: translateY(2px);
}

/* Dropdown Menu */
.user-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 280px;
  background: var(--theme-bg-secondary);
  border: 1px solid var(--theme-border-color);
  border-radius: 16px;
  box-shadow: var(--theme-shadow-xl);
  overflow: hidden;
  z-index: 1000;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--theme-hover-bg);
}

.dropdown-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(13, 78, 140, 0.5);
  background: var(--theme-gradient-primary);
  flex-shrink: 0;
}

/* Dropdown avatar con iniciales hereda estilos de .avatar-initials */
.dropdown-avatar.avatar-initials-large {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-family: var(--app-font-family);
  text-transform: uppercase;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dropdown-info {
  flex: 1;
  min-width: 0;
}

.dropdown-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--theme-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-email {
  margin: 0.25rem 0 0 0;
  font-size: 0.8rem;
  color: var(--theme-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-divider {
  height: 1px;
  background: var(--theme-border-color);
  margin: 0.5rem 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1.25rem;
  background: transparent;
  border: none;
  color: var(--theme-text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.dropdown-item:hover {
  background: var(--theme-hover-bg-light);
  color: var(--theme-text-primary);
  text-decoration: none;
}

.dropdown-item svg {
  flex-shrink: 0;
}

.logout-btn {
  color: var(--app-color-red-light);
}

.logout-btn:hover {
  background: var(--theme-hover-bg);
  color: var(--theme-color-danger);
}

/* Transición del Dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

/* Responsive */
@media (max-width: 1295px) {
  .nav-link span {
    display: none;
  }
  
  .nav-link {
    padding: 0.625rem;
    justify-content: center;
  }

  
  .user-name {
    display: none;
  }
  
  .user-avatar-btn {
    padding: 0.375rem;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 0 1rem;
    height: 60px;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
  
  .nav-links {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: linear-gradient(135deg, rgba(26, 26, 46, 0.98) 0%, rgba(22, 33, 62, 0.98) 100%);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--theme-nav-border);
    padding: 1rem;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: all 0.3s ease;
  }
  
  .nav-links.mobile-open {
    max-height: 500px;
    opacity: 1;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
  
  .nav-link {
    padding: 1rem;
    justify-content: flex-start;
    border-radius: 10px;
    margin-bottom: 0.5rem;
  }
  
  .nav-link span {
    display: inline;
  }

  /* Iconos personalizados más grandes en el menú mobile */
  .nav-link .nav-icon {
    width: 34px;
    height: 34px;
    font-size: 34px;
  }
  
  /* Efecto más pronunciado en móvil para el item activo */
  .nav-link.router-link-exact-active {
    background: linear-gradient(90deg, rgba(13, 78, 140, 0.25) 0%, rgba(13, 78, 140, 0.1) 100%);
    border-left: 4px solid transparent;
    padding-left: calc(1rem - 4px);
  }
  
  /* Tema claro en móvil - Mismo estilo que hover */
  html.light-theme .nav-links,
  .light-theme .nav-links {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 244, 248, 0.98) 100%);
    border-bottom: 1px solid rgba(13, 78, 140, 0.2);
  }
  
  html.light-theme .nav-link,
  .light-theme .nav-link {
    color: var(--theme-text-primary);
  }
  
  html.light-theme .nav-link.router-link-exact-active,
  .light-theme .nav-link.router-link-exact-active {
    background: rgba(255, 255, 255, 0.3);
    color: var(--app-primary-blue);
    border-left: 4px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-weight: 600;
  }
  
  html.light-theme .nav-link.router-link-exact-active svg,
  .light-theme .nav-link.router-link-exact-active svg,
  html.light-theme .nav-link.router-link-exact-active .nav-icon,
  .light-theme .nav-link.router-link-exact-active .nav-icon {
    color: var(--app-primary-blue);
  }

  html.light-theme .nav-link.router-link-exact-active img.nav-icon,
  .light-theme .nav-link.router-link-exact-active img.nav-icon {
    filter: brightness(0) saturate(100%) invert(20%) sepia(90%) saturate(1800%) hue-rotate(190deg) brightness(95%) contrast(95%) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }

  /* Iconos personalizados en estado inactivo: oscurecer para contraste sobre el fondo claro del menú mobile */
  html.light-theme .nav-link:not(.router-link-exact-active) img.nav-icon,
  .light-theme .nav-link:not(.router-link-exact-active) img.nav-icon {
    filter: brightness(0) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
  }
  
  .logo-img {
    width: 80px;
  }
  
  .user-name {
    display: none;
  }
  
  .user-avatar-btn {
    padding: 0.375rem;
  }
  
  .notification-dropdown {
    right: -1rem;
    width: calc(100vw - 2rem);
    max-width: 380px;
  }
  
  .dropdown-header-simple {
    padding: 1rem;
  }

  .notification-loading,
  .notification-empty {
    padding: 2rem 1.5rem;
  }

  .notification-item {
    padding: 0.875rem 1rem;
  }
}

@media (max-width: 480px) {
  .nav-container {
    gap: 0.75rem;
  }
  
  .logo-img {
    width: 70px;
  }
  
  .nav-actions,
  .nav-account {
    gap: 0.5rem;
  }
  
  .icon-btn,
  .theme-toggle-btn {
    width: 40px;
    height: 40px;
  }
  
  .icon-btn svg,
  .theme-toggle-btn svg {
    width: 18px;
    height: 18px;
  }

  .nav-link .nav-icon {
    width: 26px;
    height: 26px;
    font-size: 26px;
  }
  
  .avatar-img {
    width: 32px;
    height: 32px;
  }
  
  .avatar-initials {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  .notification-dropdown {
    width: calc(100vw - 2rem);
    right: -4.5rem;
  }

  .notification-loading,
  .notification-empty {
    padding: 1.75rem 1.25rem;
  }

  .notification-item {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .notification-icon {
    width: 36px;
    height: 36px;
  }

  .notification-title {
    font-size: 0.85rem;
  }

  .notification-message {
    font-size: 0.8rem;
  }

  .notification-empty i {
    font-size: 2.5rem;
  }
}
</style>
